import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, TrendingUp, Target } from "lucide-react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const features = {
  narvaro: [
    "Konsekvent, professionell närvaro i sociala medier",
    "Innehållsplan & publicering varje vecka (tonalitet, bildspråk, tempo)",
    "Meta-annonsering – grundnivå för räckvidd/engagemang",
    "Månadsöversikt med tydliga takeaways",
  ],
  tillvaxt: [
    "Sammanhållen content-story som fångar varumärkets kärna",
    "Datadriven annonsarkitektur (målgrupper, hypoteser, A/B-test)",
    "Trendanalys (månatlig): plattformstrender, konkurrentcase & hooks → nästa månads manus/tester",
    "Månadsrapport med insikter → åtgärd; optimering av content utifrån annonsdata",
  ],
  partner: [
    "Extern marknadsavdelning: idé → produktion → distribution → analys",
    "Full‑funnel Meta (prospecting, retargeting, lookalikes) med creative sprints",
    "Trendanalys & omvärld (månad + kvartal): prioriterade experiment i kreativa spår",
    "Contentbank + 3–4 publiceringar/vecka, KPI‑rapport & kvartalsworkshop",
  ],
};

const packages = [
  {
    key: "narvaro",
    name: "Närvaro",
    tagline: "Stabil bas • enhetligt uttryck • klar och konsekvent närvaro",
    price: 14900,
    icon: CheckCircle2,
    popular: false,
  },
  {
    key: "tillvaxt",
    name: "Tillväxt",
    tagline: "Från synlighet till konverteringsklar närvaro – kreativitet + prestanda",
    price: 26900,
    popular: true,
    icon: TrendingUp,
  },
  {
    key: "partner",
    name: "Partner",
    tagline: "Brand building + performance med en tydlig spelplan",
    price: 49000,
    icon: Target,
    popular: false,
  },
] as const;

function Price({ value }: { value: number }) {
  return (
    <div className="flex items-end gap-1">
      <span className="text-sm opacity-80">fr.</span>
      <span className="text-3xl font-extrabold tracking-tight">{value.toLocaleString("sv-SE")}</span>
      <span className="text-sm opacity-80">kr/mån</span>
    </div>
  );
}

export default function PaketPris() {
  const location = useLocation();
  
  return (
    <>
      <Header />
      <main className="pt-20">
        <section className="relative w-full overflow-hidden bg-primary">
          {/* Decorative blur blobs */}
          <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full blur-3xl bg-accent/20" />
          <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full blur-3xl bg-primary/40" />

          {/* Container */}
          <div className="mx-auto max-w-7xl px-4 py-12 sm:py-16 lg:py-20">
            {/* Header / brand */}
            <header className="mb-10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 overflow-hidden rounded-full ring-2 ring-white/20">
                  <img
                    src="/lovable-uploads/1789bb12-3853-4a6f-85ed-b0a660e3bf2d.png"
                    alt="Story Store"
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.style.display = "none";
                    }}
                  />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/60">Story Store</p>
                  <h2 className="text-xl font-semibold text-white">Paket & tjänster</h2>
                </div>
              </div>
              <div className="hidden sm:block text-right">
                <p className="text-sm text-white/70">Helhetslösningar: strategi • content • Meta‑annonsering</p>
                <p className="text-xs text-white/50">Priser exkl. moms & annonsbudget</p>
              </div>
            </header>

            {/* Intro */}
            <div className="mb-10 grid gap-4 md:grid-cols-5">
              <div className="md:col-span-3">
                <h1 className="text-3xl font-extrabold tracking-tight text-white md:text-4xl">
                  Brand‑drivna paket för synlighet, tillväxt och effekt
                </h1>
                <p className="mt-3 max-w-xl text-white/75">
                  Vi kombinerar kreativt uttryck med mätbar prestanda. Trendanalys, testning och iteration – utan att lova mer än
                  processen kan bära.
                </p>
              </div>
              <div className="md:col-span-2 flex items-end">
                <div className="w-full rounded-2xl border border-white/10 bg-white/5 p-4 text-white/80">
                  <p className="text-sm font-medium">Transparens & ansvar</p>
                  <p className="text-sm opacity-80">
                    Vi lovar inte specifika utfall. Vi lovar en tydlig process: hypotes → test → lärdom → förbättring – varje månad.
                  </p>
                </div>
              </div>
            </div>

            {/* Pricing grid */}
            <div className="grid gap-6 md:grid-cols-3">
              {packages.map((p) => {
                const Icon = p.icon;
                const list = features[p.key as keyof typeof features];
                return (
                  <Card
                    key={p.key}
                    className="relative border-white/10 bg-white/5 backdrop-blur-xl transition hover:shadow-xl hover:shadow-black/20"
                  >
                    <CardHeader className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-accent/10 text-accent">
                            <Icon className="h-5 w-5" />
                          </span>
                          <CardTitle className="text-white">{p.name}</CardTitle>
                        </div>
                        {p.popular && (
                          <Badge className="bg-white/10 text-white hover:bg-white/20">Mest vald</Badge>
                        )}
                      </div>
                      <p className="text-sm text-white/70">{p.tagline}</p>
                      <div className="text-white">
                        <Price value={p.price} />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4 text-white/90">
                      <ul className="space-y-2">
                        {list.map((f) => (
                          <li key={f} className="flex gap-2">
                            <span className="mt-1 inline-block h-1.5 w-1.5 flex-none rounded-full bg-accent" />
                            <span className="text-sm leading-relaxed">{f}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="pt-2">
                        <Button 
                          className="w-full bg-accent hover:bg-accent/90 text-white"
                          onClick={() => {
                            if (location.pathname !== '/') {
                              window.location.href = '/#kontakt';
                            } else {
                              const targetElement = document.querySelector('#kontakt');
                              if (targetElement) {
                                targetElement.scrollIntoView({ behavior: 'smooth' });
                              }
                            }
                          }}
                        >
                          Kontakta oss
                        </Button>
                      </div>
                      <p className="pt-2 text-xs text-white/60">Två revisionsrundor per leverabel ingår. Extra spår/kampanjer offereras.</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Footnote */}
            <div className="mt-8 text-center text-xs text-white/60">
              Priser exkl. moms & annonsbudget. Utfallet påverkas av bransch, målgrupp och satsningsnivå.
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}